import React from 'react';

import Box from '@mui/material/Box';
import type { BoxProps } from '@mui/material';
import { IconButton as MuiIconButton } from '@mui/material';
import camelCase from 'lodash/camelCase';

import { useTranslation } from 'next-i18next';

import Typography from 'src/components/ui/Typography';
import Icon from 'src/components/ui/Icon';
import Avatar from 'src/components/ui/Avatar';
import ConfirmModal from 'src/components/ui/ConfirmModal';

import useModalOpenState from 'src/hooks/useModalOpenState';
import useAppRouter from 'src/hooks/useAppRouter';
import useAlerts from 'src/hooks/useAlerts';

import { routePathCompany } from 'src/constants/router';
import { colours } from 'src/constants/colours';
import type { User, Company, Medium, CompanyMembersWithTotalQuery } from 'src/types/__generated__/graphql';
import { breakpoints } from 'src/constants/breakpoints';
import { useCompanyMembersWithTotalQuery, useRemoveGroupMembershipMutation } from 'src/hooks/__generated__/queries';
import { styledComponent } from 'src/utils/styled';
import { AvatarList } from 'src/components/ui/AvatarList';
import { UserData } from 'src/components/ui/AvatarList/AvatarList';

const StyledCard = styledComponent(Box)<{
    $isOnSearchCompaniesForInvitationModal?: boolean;
    $isSelected?: boolean;
}>`
    ${({ $isSelected }) =>
        $isSelected ? `background-color: ${colours.secondary.O04};` : `background-color:transparent;`}
    ${({ $isOnSearchCompaniesForInvitationModal }) =>
        $isOnSearchCompaniesForInvitationModal
            ? `box-shadow: none`
            : `box-shadow: 0rem 0.625rem 1.25rem ${colours.blackOpacity.O08};`}
    padding: 0.75rem 1rem;
    width: 100%;
    border-radius: 0.5rem;
    margin-top: 1rem;
    cursor: pointer;
`;

const StyledLink = styledComponent(Box)`
    color: ${colours.primary.main};
    font-size: 0.875rem;
    font-weight: bold;
    text-decoration: none;
    &:hover {
        text-decoration: underline;
    }
`;

const StyledBoxImage = styledComponent(Box)`
    width: 2.5rem;
    height: 2.5rem;
    margin-right: 0.5rem;
    border: 0.125rem solid ${colours.primaryOpacity.O04};
`;

const StyledAvatar = styledComponent(Avatar)`
    border-radius: 0.125rem;
`;

const StyledTypographyName = styledComponent(Typography)`
    color: ${colours.primary.main};
    font-weight: 700;
    font-size: 0.75rem;
    width: 11rem;
    @media screen and (max-width: ${breakpoints.tabletPortrait - 1}px) {
        width: 9rem;
    }
`;

const StyledTypographyCommonRelationsAndAddress = styledComponent(Typography)`
    font-weight: 400;
    font-size: 0.75rem;
`;

const StyledTypographyRatingAndRecommendations = styledComponent(Typography)`
    color: ${colours.tertiary.main};
`;

interface Props extends Omit<BoxProps, 'onClick'> {
    agency: Company;
    extraContentDisplayed?: 'ratingsAndCompanyMembers' | 'removeButton' | 'checkMark';
    onSelection?: (agencyId: string) => void;
    isSelected?: boolean;
}

const AgencyCard: React.FC<Props> = ({ isSelected, onSelection, agency, extraContentDisplayed, ...rest }) => {
    const { t } = useTranslation(['company', 'edit-profile']);
    const router = useAppRouter();
    const { addAlert } = useAlerts();
    const defaultTake = 5;
    const [removeGroupMembershipMutation] = useRemoveGroupMembershipMutation();
    const { props: confirmModalProps, open: openconfirmModal } = useModalOpenState();

    const { data: companyMembersWithTotal } = useCompanyMembersWithTotalQuery({
        variables: {
            companyId: agency.id,
            limit: defaultTake,
            orderBy: {
                currentUserContact: 'ASC',
                mutualContacts: 'DESC',
                'user.firstName': 'ASC',
            },
        },
        fetchPolicy: 'network-only',
    });

    const handleClick = async (ev: React.MouseEvent) => {
        ev.stopPropagation();
        await router.push(`${routePathCompany}/${agency.id}/team?anchor=team-section`);
    };

    const handleOpenModalRemoveGroupMembership = (ev: React.MouseEvent) => {
        ev.stopPropagation();
        openconfirmModal();
    };

    const handleRemoveGroupMembership = async () => {
        try {
            await removeGroupMembershipMutation({
                variables: {
                    companyId: agency.id,
                },
                refetchQueries: ['GroupCompanies', 'CurrentUser'],
                awaitRefetchQueries: true,
            });
            addAlert({
                type: 'success',
                message: t('edit-profile:removeAgencySuccess', { agencyName: agency.name }),
            });
        } catch (error: unknown) {
            addAlert({
                type: 'error',
                message: (error as Error).message,
            });
        }
    };

    const handleClickCard = async () => {
        if (extraContentDisplayed === 'checkMark') {
            onSelection?.(agency.id);
        } else {
            await router.push(`${routePathCompany}/${agency.id}`);
        }
    };

    return (
        <>
            <StyledCard
                onClick={handleClickCard}
                data-target-id={`agency-card-link-${agency.id}`}
                $isSelected={isSelected}
                $isOnSearchCompaniesForInvitationModal={extraContentDisplayed === 'checkMark'}
                {...rest}
            >
                <Box display="flex" flexDirection="row">
                    <Box display="flex" flexDirection="column" width="100%">
                        <Box display="flex" flexDirection="row">
                            <StyledBoxImage>
                                <StyledAvatar
                                    size={40}
                                    image={agency.image as Medium}
                                    data-target-id="agency-avatar"
                                    isCompany
                                />
                            </StyledBoxImage>
                            <Box display="flex" flexDirection="column" width="100%">
                                <StyledTypographyName data-target-id="agency-name" noWrap>
                                    {agency.name}
                                </StyledTypographyName>
                                <Box mb={!agency.address ? '1rem' : undefined}>
                                    <Typography
                                        color={colours.primaryOpacity.O72}
                                        variant="h6"
                                        data-target-id="agency-label"
                                    >
                                        {agency.label
                                            ? t(`company:${camelCase(String(agency.label))}`)
                                            : t('common:company')}
                                    </Typography>
                                </Box>
                                {agency.address && (
                                    <StyledTypographyCommonRelationsAndAddress
                                        color={colours.addressOnAgencyCard}
                                        data-target-id="agency-address"
                                    >{`${agency.address.zipCode} ${
                                        agency.address.city?.name ?? agency.address.cityName
                                    }`}</StyledTypographyCommonRelationsAndAddress>
                                )}
                            </Box>
                            {extraContentDisplayed === 'ratingsAndCompanyMembers' && (
                                <Box ml="auto">
                                    <Box display="flex" flexDirection="row">
                                        <Box mt="0.21rem">
                                            <Icon color={colours.tertiary.main} name="Star" size="1rem" />
                                        </Box>
                                        <StyledTypographyRatingAndRecommendations data-target-id="agency-averageRatings">
                                            {agency.averageRating}
                                        </StyledTypographyRatingAndRecommendations>
                                        <StyledTypographyRatingAndRecommendations data-target-id="agency-numberOfRatings">{` (${agency.numberOfRatings})`}</StyledTypographyRatingAndRecommendations>
                                        <Box ml="0.2rem" mt="0.21rem" mr="0.2rem">
                                            <Icon name="Like" size="1rem" />
                                        </Box>
                                        <StyledTypographyRatingAndRecommendations data-target-id="agency-recommendations">
                                            {String(agency.companyAndMembersRecommendationsCount)}
                                        </StyledTypographyRatingAndRecommendations>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                        {extraContentDisplayed === 'ratingsAndCompanyMembers' && (
                            <Box display="flex" flexDirection="row" data-target-id="agency-superposed-avatar">
                                {Number(companyMembersWithTotal?.companyMembersWithTotal?.total) > 0 && (
                                    <Box display="flex" flexDirection="row" mr="1rem">
                                        <Box display="flex" alignItems="center" mt="0.5rem" ml="1rem">
                                            <AvatarList
                                                users={
                                                    companyMembersWithTotal?.companyMembersWithTotal?.results
                                                        ?.map((rec) => {
                                                            if (!rec) {
                                                                return null;
                                                            }
                                                            return {
                                                                id: rec.id,
                                                                fullName: rec.fullName,
                                                                initials: rec.initials,
                                                                image: rec.image,
                                                            };
                                                        })
                                                        .filter(Boolean)
                                                        .slice(0, 5) as UserData[]
                                                }
                                            />
                                        </Box>
                                    </Box>
                                )}
                                <Box display="flex" flexDirection="column" mt="0.3rem">
                                    <StyledLink onClick={handleClick} data-target-id="agency-member-team-link">
                                        {t('company:seeGroupMembers')}
                                    </StyledLink>
                                    <StyledTypographyCommonRelationsAndAddress
                                        color={colours.blackOpacity.O72}
                                        data-target-id="agency-common-relation"
                                    >
                                        {t('company:relationsWhoWorkHere', {
                                            count: agency.companyMembersWhoAreInMyContactsCount ?? 0,
                                            plurial:
                                                agency.companyMembersWhoAreInMyContactsCount &&
                                                agency.companyMembersWhoAreInMyContactsCount > 1
                                                    ? 's'
                                                    : '',
                                            plurialEn:
                                                agency.companyMembersWhoAreInMyContactsCount &&
                                                agency.companyMembersWhoAreInMyContactsCount === 1
                                                    ? ''
                                                    : 's',
                                            conjugation:
                                                agency.companyMembersWhoAreInMyContactsCount &&
                                                agency.companyMembersWhoAreInMyContactsCount > 1
                                                    ? 'nt'
                                                    : '',
                                        })}
                                    </StyledTypographyCommonRelationsAndAddress>
                                </Box>
                                <Box mt="1.1rem" ml="auto">
                                    <Icon size="1.2rem" name="ArrowRight" />
                                </Box>
                            </Box>
                        )}
                    </Box>
                    {extraContentDisplayed === 'removeButton' && (
                        <Box display="flex" flexDirection="column" justifyContent="center">
                            <MuiIconButton
                                data-target-id="agency-member-removeButton"
                                aria-label="close"
                                onClick={handleOpenModalRemoveGroupMembership}
                            >
                                <Icon name="Close" />
                            </MuiIconButton>
                        </Box>
                    )}
                    {extraContentDisplayed === 'checkMark' && isSelected && (
                        <Box
                            display="flex"
                            flexDirection="column"
                            justifyContent="center"
                            data-target-id="agency-member-checkMark"
                        >
                            <Icon name="CheckMarkBlue" />
                        </Box>
                    )}
                </Box>
            </StyledCard>
            {confirmModalProps.open && (
                <ConfirmModal
                    title={t('edit-profile:removeAffiliation')}
                    description={t('edit-profile:descriptionRemoveAffiliation', {
                        companyName: agency.name,
                        groupName: agency.group?.name,
                    })}
                    onClick={handleRemoveGroupMembership}
                    confirm={t('common:modal.confirm')}
                    {...confirmModalProps}
                />
            )}
        </>
    );
};

export default AgencyCard;
